import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  MessageCircle, Send, CheckCircle, XCircle, Clock, 
  RefreshCw, Users, FileText, Phone, AlertCircle,
  ArrowLeft, User, MessageSquare, Check, CheckCheck, Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge Component
const StatusBadge = ({ status, direction }) => {
  if (direction === 'inbound') {
    return (
      <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
        <MessageSquare className="w-3 h-3" />
        Ricevuto
      </Badge>
    );
  }
  
  const statusConfig = {
    sent: { label: 'Inviato', color: 'bg-blue-100 text-blue-700', icon: Check },
    delivered: { label: 'Consegnato', color: 'bg-green-100 text-green-700', icon: CheckCheck },
    read: { label: 'Letto', color: 'bg-green-100 text-green-700', icon: CheckCheck },
    failed: { label: 'Fallito', color: 'bg-red-100 text-red-700', icon: XCircle },
    queued: { label: 'In coda', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    received: { label: 'Ricevuto', color: 'bg-green-100 text-green-700', icon: MessageSquare },
  };
  
  const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: Clock };
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, isOutbound }) => {
  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-3`}>
      <div 
        className={`max-w-[75%] rounded-2xl px-4 py-2 ${
          isOutbound 
            ? 'bg-green-500 text-white rounded-br-sm' 
            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
        <div className={`flex items-center gap-1 mt-1 text-xs ${isOutbound ? 'text-green-100' : 'text-gray-400'}`}>
          <span>{new Date(message.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
          {isOutbound && (
            message.status === 'delivered' || message.status === 'read' 
              ? <CheckCheck className="w-3 h-3" />
              : <Check className="w-3 h-3" />
          )}
        </div>
      </div>
    </div>
  );
};

// Conversation List Item
const ConversationItem = ({ conversation, isSelected, onClick }) => {
  const lastMessage = conversation.messages[0];
  const hasUnread = conversation.unread_count > 0;
  
  return (
    <div 
      className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition ${
        isSelected ? 'bg-green-50 border-l-4 border-l-green-500' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          hasUnread ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
        }`}>
          <User className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className={`font-medium text-sm ${hasUnread ? 'text-gray-900' : 'text-gray-700'}`}>
              {conversation.lead_name || conversation.phone}
            </span>
            {hasUnread && (
              <Badge className="bg-green-500 text-white text-xs px-2 py-0.5">
                {conversation.unread_count}
              </Badge>
            )}
          </div>
          <p className={`text-xs truncate ${hasUnread ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
            {lastMessage?.direction === 'inbound' ? '' : 'Tu: '}
            {lastMessage?.message?.substring(0, 40)}...
          </p>
          <span className="text-xs text-gray-400">
            {conversation.last_message_at && new Date(conversation.last_message_at).toLocaleDateString('it-IT')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default function WhatsAppPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  // State
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [leads, setLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('conversations'); // 'conversations' or 'new'
  
  // Form state
  const [selectedLead, setSelectedLead] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [messageText, setMessageText] = useState('');
  const [replyText, setReplyText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateVariables, setTemplateVariables] = useState({});

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load initial data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, templatesRes, conversationsRes, leadsRes] = await Promise.all([
        api.get(`${API_URL}/api/whatsapp/status`),
        api.get(`${API_URL}/api/whatsapp/templates`),
        api.get(`${API_URL}/api/whatsapp/conversations`),
        api.get(`${API_URL}/api/leads?limit=100`)
      ]);
      
      setStatus(statusRes.data);
      setTemplates(templatesRes.data.templates || []);
      setConversations(conversationsRes.data.conversations || []);
      setLeads(leadsRes.data.leads || []);
    } catch (error) {
      console.error('Error loading WhatsApp data:', error);
      toast.error('Errore nel caricamento dati WhatsApp');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation]);

  // Handle lead selection
  const handleLeadSelect = (leadId) => {
    const actualLeadId = leadId === 'none' ? '' : leadId;
    setSelectedLead(actualLeadId);
    const lead = leads.find(l => l.id === actualLeadId);
    if (lead) {
      setPhoneNumber(lead.phone || '');
      setTemplateVariables(prev => ({
        ...prev,
        client_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
      }));
    } else {
      setPhoneNumber('');
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateKey) => {
    setSelectedTemplate(templateKey);
    const template = templates.find(t => t.key === templateKey);
    if (template) {
      const vars = {};
      template.required_variables.forEach(v => {
        vars[v] = templateVariables[v] || '';
      });
      setTemplateVariables(vars);
    }
  };

  // Send direct message
  const sendDirectMessage = async () => {
    if (!phoneNumber || !messageText) {
      toast.error('Inserisci numero e messaggio');
      return;
    }
    
    setSending(true);
    try {
      await api.post(`${API_URL}/api/whatsapp/send`, {
        to_phone: phoneNumber,
        message: messageText,
        lead_id: selectedLead || null
      });
      
      toast.success('Messaggio WhatsApp inviato!');
      setMessageText('');
      loadData();
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Errore invio messaggio');
    } finally {
      setSending(false);
    }
  };

  // Send reply in conversation
  const sendReply = async () => {
    if (!selectedConversation || !replyText) {
      toast.error('Scrivi un messaggio');
      return;
    }
    
    setSending(true);
    try {
      await api.post(`${API_URL}/api/whatsapp/send`, {
        to_phone: selectedConversation.phone,
        message: replyText,
        lead_id: selectedConversation.lead_id || null
      });
      
      toast.success('Messaggio inviato!');
      setReplyText('');
      loadData();
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Errore invio messaggio');
    } finally {
      setSending(false);
    }
  };

  // Send template message
  const sendTemplateMessage = async () => {
    if (!phoneNumber || !selectedTemplate) {
      toast.error('Seleziona destinatario e template');
      return;
    }
    
    setSending(true);
    try {
      await api.post(`${API_URL}/api/whatsapp/send-template`, {
        to_phone: phoneNumber,
        template_type: selectedTemplate,
        variables: templateVariables,
        lead_id: selectedLead || null
      });
      
      toast.success('Messaggio template inviato!');
      setSelectedTemplate('');
      setTemplateVariables({});
      loadData();
    } catch (error) {
      console.error('Send template error:', error);
      toast.error(error.response?.data?.detail || 'Errore invio template');
    } finally {
      setSending(false);
    }
  };

  // Mark conversation as read
  const markConversationRead = async (phone) => {
    try {
      await api.post(`${API_URL}/api/whatsapp/conversations/${encodeURIComponent(phone)}/read-all`);
      loadData();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Handle conversation selection
  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    if (conv.unread_count > 0) {
      markConversationRead(conv.phone);
    }
  };

  // Check role
  if (!user || !['admin', 'project_manager', 'administration'].includes(user.role)) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Accesso non autorizzato.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">Caricamento WhatsApp...</p>
        </div>
      </div>
    );
  }

  const currentTemplate = templates.find(t => t.key === selectedTemplate);
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-50" data-testid="whatsapp-page">
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-7 h-7 text-green-500" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">WhatsApp Business</h1>
            <p className="text-sm text-gray-500">
              {status?.configured ? `Connesso: ${status.whatsapp_number}` : 'Non configurato'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {totalUnread > 0 && (
            <Badge className="bg-green-500 text-white">
              {totalUnread} nuov{totalUnread === 1 ? 'o' : 'i'}
            </Badge>
          )}
          {status?.configured ? (
            <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              Connesso
            </Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700 flex items-center gap-1">
              <XCircle className="w-4 h-4" />
              Non configurato
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-whatsapp">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Conversations List */}
        <div className="w-80 border-r bg-white flex flex-col">
          <div className="p-3 border-b">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="conversations" className="text-sm">
                  Conversazioni {totalUnread > 0 && `(${totalUnread})`}
                </TabsTrigger>
                <TabsTrigger value="new" className="text-sm">Nuovo</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          <ScrollArea className="flex-1">
            {activeTab === 'conversations' ? (
              conversations.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Nessuna conversazione</p>
                </div>
              ) : (
                conversations.map((conv, idx) => (
                  <ConversationItem
                    key={conv.phone || idx}
                    conversation={conv}
                    isSelected={selectedConversation?.phone === conv.phone}
                    onClick={() => handleSelectConversation(conv)}
                  />
                ))
              )
            ) : (
              <div className="p-4 space-y-4">
                <div>
                  <Label>Seleziona Lead</Label>
                  <Select value={selectedLead || 'none'} onValueChange={handleLeadSelect}>
                    <SelectTrigger data-testid="lead-selector">
                      <SelectValue placeholder="Cerca un lead..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Nessun lead --</SelectItem>
                      {leads.map(lead => (
                        <SelectItem key={lead.id} value={lead.id}>
                          {lead.first_name} {lead.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Numero di Telefono *</Label>
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+39 333 1234567"
                    data-testid="phone-input"
                  />
                </div>

                <Tabs defaultValue="direct" className="space-y-3">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="direct" className="text-xs">Diretto</TabsTrigger>
                    <TabsTrigger value="template" className="text-xs">Template</TabsTrigger>
                  </TabsList>

                  <TabsContent value="direct" className="space-y-3">
                    <Textarea
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder="Scrivi il messaggio..."
                      rows={4}
                      data-testid="message-input"
                    />
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700" 
                      onClick={sendDirectMessage}
                      disabled={sending || !phoneNumber || !messageText}
                      data-testid="send-direct-btn"
                    >
                      {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Invia
                    </Button>
                  </TabsContent>

                  <TabsContent value="template" className="space-y-3">
                    <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                      <SelectTrigger data-testid="template-selector">
                        <SelectValue placeholder="Scegli template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map(t => (
                          <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {currentTemplate && currentTemplate.required_variables.map(varName => (
                      <div key={varName}>
                        <Label className="text-xs">{varName}</Label>
                        <Input
                          value={templateVariables[varName] || ''}
                          onChange={(e) => setTemplateVariables(prev => ({...prev, [varName]: e.target.value}))}
                          placeholder={varName}
                        />
                      </div>
                    ))}

                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700" 
                      onClick={sendTemplateMessage}
                      disabled={sending || !phoneNumber || !selectedTemplate}
                      data-testid="send-template-btn"
                    >
                      {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Invia Template
                    </Button>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-[#e5ddd5]">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 bg-[#075e54] text-white flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20 lg:hidden"
                  onClick={() => setSelectedConversation(null)}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">{selectedConversation.lead_name || selectedConversation.phone}</p>
                  <p className="text-xs text-white/70">{selectedConversation.phone}</p>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-1">
                  {[...selectedConversation.messages].reverse().map((msg, idx) => (
                    <MessageBubble 
                      key={msg.id || idx} 
                      message={msg} 
                      isOutbound={msg.direction !== 'inbound'}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Reply Input */}
              <div className="p-3 bg-[#f0f0f0] border-t flex items-center gap-2">
                <Input
                  className="flex-1"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Scrivi un messaggio..."
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                  data-testid="reply-input"
                />
                <Button 
                  className="bg-[#075e54] hover:bg-[#064e46]"
                  onClick={sendReply}
                  disabled={sending || !replyText}
                  data-testid="send-reply-btn"
                >
                  {sending ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Seleziona una conversazione</p>
                <p className="text-sm">o inizia una nuova chat</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Webhook Info Banner */}
      <div className="p-3 bg-blue-50 border-t flex items-center gap-2 text-sm text-blue-700">
        <Info className="w-4 h-4 flex-shrink-0" />
        <span>
          <strong>Webhook URL:</strong> {API_URL}/api/whatsapp/webhook - 
          Configura questo URL in Twilio Console → Messaging → WhatsApp Sandbox per ricevere messaggi.
        </span>
      </div>
    </div>
  );
}
